import { useMemo, useState } from 'preact/hooks';

import { cx, PICTO_GROUPS, PictoImg, type PictoCategory, type PictoGroup } from '@amumin/ui-core';

import { Input } from './Input.js';
import styles from './IconPicker.module.css';
import { matchesPicto, normalizePictoQuery as normalize } from './pictoSearch.js';
import { SegmentedControl } from './SegmentedControl.js';

export interface IconPickerLabels {
  label: string;
  search: string;
  none: string;
  /** Met `{query}`. */
  noResults: string;
  all: string;
  /** "+{count} meer" onder een groep in Alles (opent die groep). Zonder: "+{count}". */
  more?: string;
  /** Namen per pictogram-id (i18n `design.picto`). */
  picto: Readonly<Record<string, string>>;
  /**
   * Extra zoekwoorden per pictogram-id, spatiegescheiden (i18n `design.pictoKeywords`): "koran"
   * vindt de moskee, "gebed" het bidden. Optioneel.
   */
  keywords?: Readonly<Record<string, string>>;
  /** Namen per categorie (i18n `design.pictoCategory`). */
  category: Readonly<Record<PictoCategory, string>>;
}

/**
 * 0024 (docs/DESIGN.md §8.P.5): pictogrammen die bij dit gezin uit het zicht staan. De server
 * bepaalt wat en waarom; de picker toont ze niet in het raster, wel via "Alles tonen" (gegroepeerd
 * per reden, één kop per groep met een link naar Wijzig) en in de zoekresultaten (onderaan, met de
 * reden). Kiezen mag altijd: nooit blokkeren.
 */
export interface IconPickerHidden {
  items: ReadonlyArray<{
    id: string;
    /** Groep (één kop per groep na "Alles tonen"). */
    groupKey: string;
    groupLabel: string;
    /** Reden bij een zoektreffer ("Verborgen omdat jullie halal eten. Wijzig in Instellingen."). */
    reason: string;
  }>;
  /** Getal naast "Alles tonen"; 0 = geen regel (zonder profiel niets nieuws; zoeken vindt ze). */
  badge: number;
  labels: {
    /** "3 opties verborgen" (met meervoud). */
    count: (n: number) => string;
    showAll: string;
    showLess: string;
    change: string;
    /** Kop boven de verborgen zoektreffers. */
    searchHidden: string;
  };
  /** Waar Wijzig heen gaat (Instellingen · Wat past bij ons gezin). */
  changeHref: string;
}

export interface IconPickerProps {
  value: string | null;
  onChange: (id: string | null) => void;
  labels: IconPickerLabels;
  /**
   * Welke groepen, in deze volgorde (standaard alles behalve avatars; de avatarkiezer geeft
   * ['avatar']). De eerste groep is die van de context (klusjes bij schoonmaak, boodschappen bij
   * de boodschappenlijst) en staat bovenaan.
   */
  groups?: ReadonlyArray<PictoCategory>;
  /** Toon een "Geen pictogram"-optie. */
  allowNone?: boolean;
  name?: string;
  /** 0024: verborgen pictogrammen (zie IconPickerHidden); zonder = alles zichtbaar. */
  hidden?: IconPickerHidden | null;
}

/** Pictogrammen per groep in Alles (zonder zoeken); de rest achter "+n meer". */
const PER_GROUP = 12;

/**
 * Pictogramkiezer: zoeken op Nederlandse naam, filter per categorie, raster van 56 px-tegels.
 * Beeld komt uit /art/picto (statisch), de namen uit i18n.
 */
export function IconPicker({
  value,
  onChange,
  labels,
  groups,
  allowNone = true,
  name = 'icon-picker',
  hidden = null,
}: IconPickerProps) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<PictoCategory | 'all'>('all');
  const [showAll, setShowAll] = useState(false);
  const available: ReadonlyArray<PictoGroup> = useMemo(() => {
    if (!groups) return PICTO_GROUPS.filter((g) => g.id !== 'avatar');
    const out: PictoGroup[] = [];
    for (const id of groups) {
      const g = PICTO_GROUPS.find((x) => x.id === id);
      if (g) out.push(g);
    }
    return out;
  }, [groups]);
  const q = normalize(query.trim());
  const hiddenIds = new Set((hidden?.items ?? []).map((h) => h.id));
  const inScope = new Set<string>();
  for (const g of available) {
    if (category === 'all' || g.id === category) for (const id of g.ids) inScope.add(id);
  }
  const visible = available
    .filter((g) => category === 'all' || g.id === category)
    .map((g) => ({
      ...g,
      ids: g.ids.filter((id) => !hiddenIds.has(id) && (!q || matchesPicto(id, q, labels))),
    }))
    .filter((g) => g.ids.length > 0);
  // Verborgen in deze selectie: voor "Alles tonen" en als zoektreffer (onderaan, met reden).
  const hiddenHere = (hidden?.items ?? []).filter((h) => inScope.has(h.id));
  const hiddenMatches = q ? hiddenHere.filter((h) => matchesPicto(h.id, q, labels)) : [];
  const hiddenGroups: Array<{ key: string; label: string; ids: string[] }> = [];
  for (const h of hiddenHere) {
    const g = hiddenGroups.find((x) => x.key === h.groupKey);
    if (g) g.ids.push(h.id);
    else hiddenGroups.push({ key: h.groupKey, label: h.groupLabel, ids: [h.id] });
  }

  // Geen eigen scroll (6 okt 2026, docs/ux/ontwerpsysteem.md regel 13): in Alles toont elke groep
  // hooguit PER_GROUP pictogrammen en dan "+n meer", dat die groep opent. Het blad eromheen scrolt.
  const sample = category === 'all' && !q && available.length > 1;

  const tile = (id: string, dim: boolean) => {
    const label = labels.picto[id] ?? id;
    return (
      <button
        key={id}
        type="button"
        role="radio"
        aria-checked={value === id ? 'true' : 'false'}
        class={cx(styles.tile, value === id && styles.selected, dim && styles.dim)}
        title={label}
        onClick={() => onChange(id)}
        data-picto={id}
        data-hidden={dim ? 'true' : undefined}
      >
        <PictoImg id={id} size={40} loading="lazy" />
        <span class={styles.tileLabel}>{label}</span>
      </button>
    );
  };

  return (
    <div class={styles.picker} data-name={name}>
      <div class={styles.search}>
        <Input
          id={`${name}-search`}
          type="search"
          icon="search"
          value={query}
          onInput={setQuery}
          placeholder={labels.search}
          label={labels.search}
        />
      </div>
      {available.length > 1 ? (
        <div class={styles.filters}>
          <SegmentedControl
            label={labels.label}
            size="small"
            value={category}
            onChange={setCategory}
            options={[
              { value: 'all' as const, label: labels.all },
              ...available.map((g) => ({ value: g.id, label: labels.category[g.id] })),
            ]}
          />
        </div>
      ) : null}
      <div class={styles.scroll} role="radiogroup" aria-label={labels.label}>
        {allowNone && !q ? (
          <button
            type="button"
            role="radio"
            aria-checked={value === null ? 'true' : 'false'}
            class={cx(styles.tile, styles.none, value === null && styles.selected)}
            onClick={() => onChange(null)}
          >
            <span class={styles.noneMark} aria-hidden="true" />
            <span class={styles.tileLabel}>{labels.none}</span>
          </button>
        ) : null}
        {/* Het huidige pictogram staat vooraan en is gekozen (ook als het in een latere groep zit). */}
        {value && !q ? (
          <button
            type="button"
            role="radio"
            aria-checked="true"
            class={cx(styles.tile, styles.selected)}
            title={labels.picto[value] ?? value}
            onClick={() => onChange(value)}
            data-name={`${name}-current`}
          >
            <PictoImg id={value} size={40} />
            <span class={styles.tileLabel}>{labels.picto[value] ?? value}</span>
          </button>
        ) : null}
        {visible.length === 0 && hiddenMatches.length === 0 ? (
          <p class={styles.empty}>{labels.noResults.replace('{query}', query.trim())}</p>
        ) : (
          visible.map((g) => (
            <section key={g.id} class={styles.group}>
              {category === 'all' ? (
                <h3 class={styles.groupTitle}>{labels.category[g.id]}</h3>
              ) : null}
              <div class={styles.grid}>
                {(() => {
                  // Het huidige pictogram staat al vooraan.
                  const ids = g.ids.filter((id) => q || id !== value);
                  const shown = sample ? ids.slice(0, PER_GROUP) : ids;
                  const rest = ids.length - shown.length;
                  return (
                    <>
                      {shown.map((id) => tile(id, false))}
                      {rest > 0 ? (
                        <button
                          type="button"
                          class={cx(styles.tile, styles.moreTile)}
                          onClick={() => setCategory(g.id)}
                          data-name={`${name}-more-${g.id}`}
                        >
                          {(labels.more ?? '+{count}').replace('{count}', String(rest))}
                        </button>
                      ) : null}
                    </>
                  );
                })()}
              </div>
            </section>
          ))
        )}
        {hidden && hiddenMatches.length > 0 ? (
          <section class={styles.group} data-name={`${name}-hidden-matches`}>
            <h3 class={styles.groupTitle}>{hidden.labels.searchHidden}</h3>
            <ul class={styles.reasonList}>
              {hiddenMatches.map((h) => (
                <li key={h.id} class={styles.reasonRow}>
                  {tile(h.id, true)}
                  <span class={styles.reason}>{h.reason}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        {hidden && !q && hidden.badge > 0 ? (
          <div class={styles.hiddenBlock} data-name={`${name}-hidden`}>
            <p class={styles.hiddenRow}>
              <span>{hidden.labels.count(hidden.badge)}</span>
              <span aria-hidden="true"> · </span>
              <button
                type="button"
                class={styles.linkButton}
                aria-expanded={showAll ? 'true' : 'false'}
                onClick={() => setShowAll(!showAll)}
                data-name={`${name}-show-all`}
              >
                {showAll ? hidden.labels.showLess : hidden.labels.showAll}
              </button>
            </p>
            {showAll
              ? hiddenGroups.map((g) => (
                  <section key={g.key} class={styles.group} data-name={`${name}-hidden-group`}>
                    <h3 class={styles.hiddenHead}>
                      {g.label}
                      <span aria-hidden="true"> · </span>
                      <a href={hidden.changeHref} class={styles.change}>
                        {hidden.labels.change}
                      </a>
                    </h3>
                    <div class={styles.grid}>{g.ids.map((id) => tile(id, true))}</div>
                  </section>
                ))
              : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
